import { useState } from "react";
import AdPlanModal from "../../../core/blocks/AdPlan/AdPlanModal";
import { AD_TYPES, GOALS } from "../../../core/blocks/AdPlan/constants";
import moment from "moment";
import { Row, Col } from "react-bootstrap";
import { formatNumber } from "../../../../utils/formatNumber";
import { Link } from "@tanstack/react-router";
import AdvertisingIcon from "../../../../assets/images/icons/placement.svg";
import PricingIcon from "../../../../assets/images/icons/pricing.svg";
import checkTierAccess from "../../../../utils/checkTierAccess";
import { Button } from "../../../core/basic/Button";
import { Checkbox } from "../../../fields/base/Checkbox";
import { useTranslation } from "react-i18next";

const LaunchTask = ({ task: { title, description, cta, icon }, completed, hideIcon = false, small, last }) => {
  const { t } = useTranslation(["merchandising"]);
  return (
    <Row>
      <Col xs={12} md={12}>
        <div
          className={small ? "" : "card"}
          style={{
            borderBottom: small && !last ? "1px solid #CCC" : "none",
            marginBottom: (small && last) || !small ? "2rem" : "inherit",
          }}
        >
          <div className="card-body" style={{ padding: small ? "1rem" : "2rem 4rem" }}>
            <Row style={{ display: "flex", paddingBottom: "2rem" }}>
              <Col xs={8}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ pointerEvents: "none", marginTop: "-30px" }}>
                    <Checkbox value={completed} />
                  </span>

                  <h3
                    style={{
                      margin: "2rem 0",
                      fontSize: "2.5rem",
                      color: "#403E3D",
                      fontWeight: "700",
                      paddingLeft: "1rem",
                    }}
                  >
                    {title}
                  </h3>
                </div>
                <Row style={{ paddingLeft: 0 }}>
                  {!hideIcon && (
                    <Col xs={1}>
                      <img
                        src={icon}
                        alt={t(($) => $.merchandising["An icon with a rocket ship launching into space."])}
                        style={{
                          height: "80px",
                          width: "80px",
                        }}
                      />
                    </Col>
                  )}
                  <Col xs={11} style={{ paddingLeft: "2rem" }} className="d-flex flex-column justify-content-center">
                    {description}
                  </Col>
                </Row>
              </Col>
              <Col
                xs={4}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingTop: "1rem",
                }}
              >
                {cta}
              </Col>
            </Row>
          </div>
        </div>
      </Col>
    </Row>
  );
};

const LaunchPlanningDialogue = ({ launchData, categories, user, amz_onboard }) => {
  const [adPlanOpen, setAdPlanOpen] = useState(false);
  const { t } = useTranslation(["merchandising"]);
  const nextSteps = [];

  nextSteps.push({
    title: t(($) => $.merchandising["Placement Advertising"]),
    description: (
      <>
        <p style={{ color: "#73706E" }}>
          {t(($) => $.merchandising["Start by creating an advertisement to promote your products."])}
        </p>
        <p style={{ color: "#73706E" }}>
          {t(
            ($) =>
              $.merchandising[
                "We've pre-populated most fields with the goals you outlined earlier, but you can also tweak fields based on your needs."
              ],
          )}
        </p>
      </>
    ),
    cta: (
      <div style={{ minWidth: "200px" }}>
        <Button onPress={() => setAdPlanOpen(true)}>{t(($) => $.merchandising["Create Media Plan"])}</Button>
        <AdPlanModal
          isOpen={adPlanOpen}
          onClose={() => setAdPlanOpen(false)}
          categories={categories}
          initialAdPlan={{
            name: t(($) => $.merchandising["{name} Media Plan"], { name: launchData?.name }),
            adType: AD_TYPES.SPONSORED_PRODUCT.value,
            product: launchData?.products ?? [],
            goal: GOALS.PRODUCT_LAUNCH.value,
            targetACOS: launchData?.goal_metrics?.acos,
            maxACOS: launchData?.goal_metrics?.acos + 10,
            dailyBudget: formatNumber(
              launchData?.advertising_budget /
                moment(launchData?.end_date).diff(moment(launchData?.start_date), "days"),
              { decimalPlaces: 2 },
            ),
            categoryId: launchData?.category,
          }}
        />
      </div>
    ),
    icon: AdvertisingIcon,
  });

  if (
    (!checkTierAccess(user.tier, "pricing", amz_onboard.org_status) ||
      (user.pricing_tier && user.pricing_tier !== "unknown")) &&
    user.pricing_tier !== "MANUALLY_DISABLED"
  ) {
    nextSteps.push({
      title: t(($) => $.merchandising["Pricing"]),
      description: (
        <>
          <p style={{ color: "#73706E" }}>
            {t(($) => $.merchandising["Boost your products popularity by optimizing price."])}
          </p>
          <p style={{ color: "#73706E" }}>
            {t(
              ($) =>
                $.merchandising[
                  "By tracking and understanding your users' behavior, Trellis can optimize for sales growth, profit margins, or net profit."
                ],
            )}
          </p>
        </>
      ),
      cta: (
        <div style={{ minWidth: "200px" }}>
          <Link to="/user/pricing/dashboard" style={{ textDecoration: "none" }}>
            <Button>{t(($) => $.merchandising["View Pricing"])}</Button>
          </Link>
        </div>
      ),
      icon: PricingIcon,
    });
  }

  return (
    <Col xs={12}>
      <Row className="ps-5">
        {nextSteps.map((task) => (
          <LaunchTask key={task.title} task={task} completed={false} />
        ))}
      </Row>
    </Col>
  );
};

export default LaunchPlanningDialogue;
